import {Image, Pressable} from "react-native"
import isUserA, {isUserOnSchoolA} from "components/users/is-a"
import {memo, PropTypes, propTypesExact, shapeComponent, ShapeComponent, Text, useBreakpoint, useI18n, View} from "shared/base"
import AppHistory from "shared/history"
import CountrySelect from "components/front-layout/country-select"
import Devise from "@kaspernj/api-maker/build/devise"
import {FlashNotifications as FlashMessage} from "flash-notifications"
import Icon from "@kaspernj/api-maker/build/utils/icon"
import LocaleSelect from "components/front-layout/locale-select"
import logo from "./logo.svg"
import MenuItem from "./menu-item"
import React from "react"
import Routes from "shared/routes"
import {Subscription} from "models"
import useAnythingPro from "components/subscriptions/use-anything-pro"

/** Sidebar menu used on chalky user layout screens. */
export default memo(shapeComponent(class ComponentsChalkyLayoutMenu extends ShapeComponent {
  static propTypes = propTypesExact({
    active: PropTypes.string,
    anythingPro: PropTypes.bool,
    anythingProState: PropTypes.object,
    currentUser: PropTypes.object,
    hasPro: PropTypes.bool,
    onSignedOut: PropTypes.func,
    onRequestClose: PropTypes.func.isRequired,
    onUpgradePressed: PropTypes.func.isRequired,
    showMenu: PropTypes.bool.isRequired,
    userSubscriptionPlanIdentifier: PropTypes.string
  })

  /** @returns {void} */
  setup() {
    const {t} = useI18n({namespace: "js.components.chalky_layout.menu"})
    const {currentUser} = this.props
    const instance = this
    const anythingProState = useAnythingPro({currentUser})

    this.useStates({
      hasProValue: undefined,
      userSubscriptionPlanIdentifierValue: undefined,
      userSubscriptionStripeSubscriptionIdValue: undefined
    })

    React.useEffect(() => {
      let isActive = true

      if (!currentUser?.id?.()) {
        instance.setState({
          hasProValue: undefined,
          userSubscriptionPlanIdentifierValue: undefined,
          userSubscriptionStripeSubscriptionIdValue: undefined
        })


        return () => {
          isActive = false
        }
      }

      const loadHasPro = async () => {
        try {
          const result = await Subscription.hasPro({user_id: currentUser.id()})
          const normalizedResult = result?.result || result
          const resolvedHasPro = normalizedResult?.has_pro ?? normalizedResult?.hasPro
          const resolvedPlanIdentifier = normalizedResult?.user_subscription_plan_identifier ?? normalizedResult?.userSubscriptionPlanIdentifier
          const resolvedStripeSubscriptionIdFromResult =
            normalizedResult?.user_subscription_stripe_subscription_id ?? normalizedResult?.userSubscriptionStripeSubscriptionId
          const userSubscription = normalizedResult?.user_subscription ?? normalizedResult?.userSubscription
          const resolvedStripeSubscriptionId =
            resolvedStripeSubscriptionIdFromResult || userSubscription?.stripe_subscription_id || userSubscription?.stripeSubscriptionId

          if (isActive) {
            instance.setState({
              hasProValue: resolvedHasPro,
              userSubscriptionPlanIdentifierValue: resolvedPlanIdentifier,
              userSubscriptionStripeSubscriptionIdValue: resolvedStripeSubscriptionId
            })
          }
        } catch (error) {
          if (isActive) {
            instance.setState({
              hasProValue: false,
              userSubscriptionPlanIdentifierValue: undefined,
              userSubscriptionStripeSubscriptionIdValue: undefined
            })
          }
        }
      }

      loadHasPro()

      return () => {
        isActive = false
      }
    }, [currentUser?.id?.()])

    this.t = t
    this.setInstance(useBreakpoint())
    this.setInstance({anythingProState})
  }

  /** @returns {React.JSX.Element} */
  render() { // eslint-disable-line complexity
    const {mdUp, t} = this.tt
    const {
      active,
      anythingPro: anythingProProp,
      currentUser,
      hasPro,
      onRequestClose,
      showMenu,
      userSubscriptionPlanIdentifier
    } = this.p
    const hasProValueFromState = this.s.hasProValue
    const userSubscriptionPlanIdentifierFromState = this.s.userSubscriptionPlanIdentifierValue
    const userSubscriptionStripeSubscriptionIdFromState = this.s.userSubscriptionStripeSubscriptionIdValue
    const anythingProState = this.tt.anythingProState || this.p.anythingProState || {}
    const hasProValue = hasProValueFromState ?? hasPro
    const userSubscriptionPlanIdentifierValue = userSubscriptionPlanIdentifierFromState ?? userSubscriptionPlanIdentifier
    let paddingLeft, width
    const {schoolClassesWithPro, schoolsThroughClassWithSchoolPro, schoolsWithPro, userProSubscriptions} = anythingProState
    const computedAnythingPro = Boolean(
      anythingProState?.anythingPro ||
      anythingProState?.hasProTrial ||
      anythingProState?.userProSubscriptions?.length > 0 ||
      anythingProState?.schoolsWithPro?.length > 0 ||
      anythingProState?.schoolClassesWithPro?.length > 0 ||
      anythingProState?.schoolsThroughClassWithSchoolPro?.length > 0
    )
    const hasProTrial = Boolean(anythingProState?.hasProTrial)
    const hasNonTrialUserSubscription = userProSubscriptions?.some((subscription) => {
      const plan = subscription.plan?.()
      const planIdentifier = plan?.identifier?.()

      return planIdentifier && planIdentifier != "pro_trial"
    })
    const hasNonTrialProViaHasPro = hasProValue && userSubscriptionPlanIdentifierValue != "pro_trial"
    // eslint-disable-next-line no-underscore-dangle
    const hasNonTrialProGlobal = Boolean(globalThis.__hasNonTrialPro)
    const showSubscriptionAndBilling = Boolean(userSubscriptionStripeSubscriptionIdFromState)
    const shouldHideUpgradeForPro = computedAnythingPro && !hasProTrial
    const showProUpgrade = !(
      schoolsWithPro?.length > 0 ||
      schoolClassesWithPro?.length > 0 ||
      schoolsThroughClassWithSchoolPro?.length > 0 ||
      hasNonTrialUserSubscription ||
      hasNonTrialProViaHasPro ||
      hasNonTrialProGlobal ||
      shouldHideUpgradeForPro
    )
    // eslint-disable-next-line no-underscore-dangle
    const globalAnythingPro = Boolean(globalThis.__anythingPro)
    const anythingPro = Boolean(anythingProProp) || computedAnythingPro || globalAnythingPro || Boolean(currentUser)

    if (showMenu) {
      if (mdUp) {
        paddingLeft = 48
        width = 240
      } else {
        paddingLeft = 24
        width = 190
      }
    } else {
      paddingLeft = 48
      width = 240
    }

    return (
      <View
        dataSet={this.cache("menuDataSet", {anythingPro, class: "menu"}, [anythingPro])}
        style={this.cache("menuStyle", {
          position: "fixed",
          zIndex: 9,
          top: showMenu ? 64 : 0,
          left: showMenu ? 0 : 0,
          width,
          height: showMenu ? "calc(100vh - 128px)" : "100%",
          paddingTop: showMenu ? undefined : 50,
          borderRight: "1px solid #cbc6ff",
          backgroundColor: showMenu ? undefined : "#fff"
        }, [mdUp, showMenu, width])}
      >
        {!showMenu &&
            <Pressable onPress={onRequestClose} style={this.closeMenuPressableStyle ||= {position: "absolute", top: 20, right: 20}}>
              <Icon color="#000" name="remove" size={20} />
            </Pressable>
        }
        <Image
          dataSet={this.menuLogoDataSet ||= {class: "menu-logo"}}
          resizeMode="contain"
          source={logo}
          style={this.menuLogoStyle ||= {
            marginHorizontal: "auto",
            marginBottom: 50,
            width: 144,
            height: 40
          }}
        />
        <View style={this.cache("menuItemsContainer", {flex: 1, height: "100%", paddingLeft}, [paddingLeft])}>
          <View style={this.menuItemContainerStyle ||= {marginBottom: 25}}>
            <MenuItem
              active={active == "dashboard"}
              icon="table-cells-large"
              iconVersion="FontAwesome6"
              identifier="dashboard"
              label={t(".my_classrooms")}
              to={Routes.userRootPath()}
            />
          </View>
          <View style={this.menuItemContainerStyle ||= {marginBottom: 25}}>
            <MenuItem
              active={active == "user-class-status"}
              icon="temperature-half"
              iconVersion="FontAwesome6"
              identifier="user-class-status"
              label={t(".my_status")}
              to={Routes.userClassStatusPath()}
            />
          </View>
          <View style={this.menuItemContainerStyle ||= {marginBottom: 25}}>
            <MenuItem
              active={active == "user-unicorn"}
              icon="chart-simple"
              iconVersion="FontAwesome6"
              identifier="user-unicorn"
              label={t(".my_insights")}
              to={Routes.userUnicornPath()}
            />
          </View>
          {isUserA(currentUser, "tester") &&
              <View style={this.menuItemContainerStyle ||= {marginBottom: 25}}>
                <MenuItem
                  active={active == "user-class-profile"}
                  icon="chart-simple"
                  iconVersion="FontAwesome6"
                  identifier="user-class-profile"
                  label={t(".class_profile")}
                  to={Routes.userClassProfilePath()}
                />
              </View>
          }
          {isUserOnSchoolA(currentUser, {identifiers: ["admin", "school_leader"]}) &&
              <View style={this.menuItemContainerStyle ||= {marginBottom: 25}}>
                <MenuItem
                  active={active == "my-users"}
                  icon="chart-simple"
                  iconVersion="FontAwesome6"
                  identifier="my-users"
                  label={t(".my_users")}
                  to={Routes.userMyUsersPath()}
                />
              </View>
          }
          <View style={this.cache("bottomContainerStyle", {marginTop: "auto", marginBottom: 30, paddingRight: paddingLeft}, [paddingLeft])}>
            {showProUpgrade &&
                <View style={this.upgradeViewStyle ||= {width: "100%", flexDirection: "row", marginBottom: 15}}>
                  <Pressable
                    onPress={this.p.onUpgradePressed}
                    style={this.upgradeButtonStyle ||= {width: "100%"}}
                    testID="side-bar-menu-upgrade-pressable"
                  >
                    <Text
                      style={this.upgradeButtonTextStyle ||= {
                        paddingTop: 11,
                        paddingRight: 20,
                        paddingBottom: 11,
                        paddingLeft: 20,
                        backgroundColor: "#d648dc",
                        borderRadius: 6,
                        color: "#fff",
                        fontSize: 16,
                        textAlign: "center"
                      }}
                    >
                      {t(".upgrade")}
                    </Text>
                  </Pressable>
                </View>
            }
            <View style={{marginBottom: 5}}>
              <CountrySelect />
            </View>
            <View>
              <LocaleSelect />
            </View>
          </View>
          {showSubscriptionAndBilling &&
            <MenuItem
              icon="credit-card"
              iconVersion="FontAwesome6"
              identifier="subscription-and-billing-menu-item"
              label={t(".subscription_and_billing")}
              onPress={this.tt.onSubscriptionAndBillingPress}
              testID="sidebar-subscription-and-billing-menu-item"
            />
          }
          <MenuItem
            icon="arrow-right-from-bracket"
            iconVersion="FontAwesome6"
            identifier="sign-out-menu-item"
            label={t(".sign_out")}
            onPress={this.tt.onSignOutPress}
          />
        </View>
      </View>
    )
  }

  /** @returns {Promise<void>} */
  onSubscriptionAndBillingPress = async () => {
    try {
      const result = await Subscription.customerPortalUrl({return_url: globalThis.location?.href || globalThis.location?.origin || ""})
      const normalizedResult = result?.result || result
      const url = normalizedResult?.url

      if (!url) {
        throw new Error("Stripe customer portal URL was missing from command response.")
      }

      globalThis.location.assign(url)
    } catch (error) {
      FlashMessage.errorResponse(error)
    }
  }

  /** @returns {Promise<void>} */
  onSignOutPress = async () => {
    const {t} = this.tt

    try {
      await Devise.signOut()
      Devise.setSignedOut({scope: "user"})
      Devise.callSignOutEvent({scope: "user"})
      FlashMessage.success(t(".you_have_been_signed_out"))
      this.p.onSignedOut?.()
      AppHistory.push(Routes.rootPath())
    } catch (error) {
      FlashMessage.errorResponse(error)
    }
  }
}))
